/**
 * QueueKanbanCard - Card de conversa para Kanban da Fila de Atendimento
 * 
 * Versão customizada sem drag & drop (apenas visualização)
 * Exibe informações extras: departamento e agente que transferiu
 */

import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { MessageSquare, Building2, ArrowRight, Instagram, MessagesSquare, Send, Globe, Inbox } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { getStorageUrl } from '@/lib/storage';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { getAvatarColorClasses } from '@/lib/utils/avatar-colors';
import type { QueueItem } from '@/hooks/useServiceQueue';

interface QueueKanbanCardProps {
  item: QueueItem;
  onClick?: () => void;
}

/**
 * Trunca texto para preview
 */
const truncateText = (text: string | null, maxLength: number): string => {
  if (!text) return '';
  return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
};

/**
 * Formata tempo relativo de forma compacta
 */
const formatTime = (dateStr: string | null): string => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return `${diffInSeconds}s`;
    
    // Formata tempo relativo e remove "cerca de " para economizar espaço
    const formatted = formatDistanceToNow(date, { addSuffix: false, locale: ptBR });
    return formatted.replace(/^cerca de /i, '');
  } catch {
    return '';
  }
};

/**
 * Badge pequeno do canal exibido sobre o avatar do card.
 * Lógica idêntica ao ConversationList: WhatsApp verde, WABA preto/branco,
 * Instagram rosa, etc. Mantém o WABA restrito a channel_type === 'whatsapp'
 * para não classificar Instagram (meta-instagram) erroneamente.
 */
const getChannelBadge = (
  channelType?: string | null,
  providerSlug?: string | null,
) => {
  if (!channelType) return null;

  const isWaba =
    channelType === 'whatsapp_oficial' ||
    (channelType === 'whatsapp' &&
      !!providerSlug &&
      /meta|waba|cloud/i.test(providerSlug));

  // Glifo SVG do WhatsApp (reutilizado para verde e preto/branco)
  const WhatsAppGlyph = ({ color }: { color: string }) => (
    <svg viewBox="0 0 32 32" className="h-[12px] w-[12px]" aria-hidden="true">
      <path
        fill={color}
        d="M16 2.667C8.636 2.667 2.667 8.636 2.667 16c0 2.355.621 4.66 1.8 6.687L2.667 29.333l6.84-1.78A13.31 13.31 0 0 0 16 29.333C23.364 29.333 29.333 23.364 29.333 16S23.364 2.667 16 2.667Zm7.74 18.86c-.328.92-1.62 1.694-2.66 1.92-.71.15-1.638.27-4.762-1.024-3.996-1.654-6.57-5.71-6.77-5.974-.2-.264-1.6-2.124-1.6-4.05 0-1.928 1.012-2.872 1.37-3.26.296-.32.78-.464 1.246-.464.15 0 .286.008.41.014.358.016.538.036.774.602.296.71 1.006 2.464 1.094 2.644.09.18.18.426.054.69-.118.27-.224.382-.404.59-.18.21-.35.37-.53.6-.164.198-.348.41-.14.762.21.35.928 1.532 1.99 2.478 1.37 1.22 2.5 1.61 2.86 1.768.27.118.59.094.79-.118.252-.27.566-.722.886-1.166.226-.318.51-.358.812-.244.31.106 1.95.918 2.288 1.084.338.166.56.246.644.388.082.146.082.842-.244 1.62Z"
      />
    </svg>
  );

  let inner: JSX.Element;
  if (isWaba) {
    inner = (
      <span className="text-foreground inline-flex">
        <WhatsAppGlyph color="currentColor" />
      </span>
    );
  } else if (channelType === 'whatsapp') {
    inner = <WhatsAppGlyph color="#25D366" />;
  } else if (channelType === 'instagram') {
    inner = <Instagram className="h-[12px] w-[12px] text-pink-500" strokeWidth={2.5} />;
  } else if (channelType === 'messenger') {
    inner = <MessagesSquare className="h-[12px] w-[12px] text-blue-500" strokeWidth={2.5} />;
  } else if (channelType === 'telegram') {
    inner = <Send className="h-[12px] w-[12px] text-sky-500" strokeWidth={2.5} />;
  } else if (channelType === 'webchat') {
    inner = <Globe className="h-[12px] w-[12px] text-muted-foreground" strokeWidth={2.5} />;
  } else {
    inner = <Inbox className="h-[12px] w-[12px] text-muted-foreground" strokeWidth={2.5} />;
  }

  return (
    <div
      className="absolute -bottom-1 -left-1 z-10 flex h-[16px] w-[16px] items-center justify-center rounded-full bg-background border border-border shadow-sm"
      aria-label={`Canal: ${channelType}`}
    >
      {inner}
    </div>
  );
};

export function QueueKanbanCard({ item, onClick }: QueueKanbanCardProps) {
  const contact = item.contact;
  // v1.3.0: prioriza dados do grupo quando a conversa é de grupo
  const isGroup = !!item.group;
  const displayName =
    (isGroup ? item.group?.name : contact?.name) ||
    (!isGroup ? contact?.phone : null) ||
    'Contato';
  const rawAvatar = isGroup ? item.group?.avatar_url : contact?.avatar_url;
  const avatarUrl = rawAvatar ? getStorageUrl(rawAvatar) : null;
  const avatarSeed = isGroup ? item.group?.name : (contact?.name || contact?.phone);
  const timeAgo = formatTime(item.last_message_at);

  return (
    <Card
      className={cn(
        'p-3 cursor-pointer hover:shadow-md transition-all duration-200',
        'border border-border/50 bg-card overflow-hidden'
      )}
      onClick={onClick}
    >
      {/* Header: Avatar + Nome + Unread */}
      <div className="flex items-start gap-2">
        <div className="relative shrink-0">
          <Avatar className="h-9 w-9">
            <AvatarImage src={avatarUrl || undefined} alt={displayName} />
            <AvatarFallback className={cn("text-xs", getAvatarColorClasses(avatarSeed))}>
              {displayName.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          {/* Badge do canal sobre o avatar (mesma lógica da lista de conversas) */}
          {getChannelBadge(item.inbox?.channel_type, item.inbox?.provider_slug)}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-1">
            <span className="font-medium text-sm truncate max-w-[140px]">
              {truncateText(displayName, 18)}
            </span>
            {item.unread_count > 0 && (
              <span className="bg-primary text-primary-foreground text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center shrink-0">
                {item.unread_count > 99 ? '99+' : item.unread_count}
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground truncate">
            {truncateText(item.last_message_preview, 28)}
          </p>
        </div>
      </div>

      {/* Rodapé: tempo, badges (esquerda) + avatar (direita fixa) */}
      <div className="flex items-center justify-between gap-2 mt-2">
        {/* Lado esquerdo: tempo + badges (com flex-wrap) */}
        <div className="flex items-center gap-1.5 flex-wrap flex-1 min-w-0">
          {/* Tempo */}
          {timeAgo && (
            <span className="text-[10px] text-muted-foreground">{timeAgo}</span>
          )}

          {/* Inbox */}
          {item.inbox?.name && (
            <Badge variant="outline" className="h-4 text-[10px] px-1.5 max-w-[70px]">
              <MessageSquare className="h-2.5 w-2.5 mr-0.5 shrink-0" />
              <span className="truncate">{item.inbox.name}</span>
            </Badge>
          )}

          {/* Departamento */}
          {item.department?.name && (
            <Badge variant="outline" className="h-4 text-[10px] px-1.5 max-w-[70px] border-orange-500/50 text-orange-600 dark:text-orange-400">
              <Building2 className="h-2.5 w-2.5 mr-0.5 shrink-0" />
              <span className="truncate">{item.department.name}</span>
            </Badge>
          )}

          {/* Agente que transferiu (com seta) */}
          {item.pending_transfer?.from_agent_name && (
            <Badge variant="secondary" className="h-4 text-[10px] px-1.5 max-w-[80px]">
              <ArrowRight className="h-2.5 w-2.5 mr-0.5 shrink-0" />
              <span className="truncate">{item.pending_transfer.from_agent_name}</span>
            </Badge>
          )}
        </div>

        {/* Lado direito: Avatar do agente (fixo no canto) */}
        {item.assigned_agent && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="shrink-0">
                <Avatar className="h-5 w-5 border border-green-500/50">
                  <AvatarImage 
                    src={item.assigned_agent.avatar_url ? getStorageUrl(item.assigned_agent.avatar_url) : undefined} 
                    alt={item.assigned_agent.full_name || 'Agente'} 
                  />
                  <AvatarFallback className="text-[8px] bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                    {(item.assigned_agent.full_name || 'AG').slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              {item.assigned_agent.full_name || 'Agente'}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </Card>
  );
}
